import React, { useState, useEffect, useRef } from 'react';
import { Page, TextElement, ImageElement } from '../types/storybook';
import { PageLayoutTemplate } from '../types/layout';
import { TEXT_BOX_STYLES } from '../utils/textBoxStyles';
import './Toolbar.css';

interface ToolbarProps {
  onAddText: () => void;
  onAddTextBox: (styleId: string) => void;
  onAddPage: () => void;
  onDeletePage: () => void;
  canDeletePage: boolean;
  onRegeneratePage?: () => void;
  canRegeneratePage?: boolean;
  onApplySafeLayout?: () => Promise<void>;
  onApplyPageTemplate?: (template: Exclude<PageLayoutTemplate, 'custom'>) => Promise<void>;
  canApplySafeLayout?: boolean;
  isApplyingSafeLayout?: boolean;
  selectedElement: string | null;
  currentPage: Page | undefined;
  onDeleteElement: () => void;
  onUpdateTextElement: (id: string, updates: Partial<TextElement>) => void;
  onUpdateImageElement: (id: string, updates: Partial<ImageElement>) => void;
  onCropImage?: () => void;
  onRephraseText?: (textId: string, content: string) => Promise<void>;
  isRephrasing?: boolean;
  onApplyTextStyle?: (textId: string, styleId: string) => void;
  onSaveImageToScene?: (imageUrl: string) => Promise<void>;
  onUpscaleImage?: (imageUrl: string) => Promise<string>;
  existingSceneNames?: string[];
  onCheckResolution?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  onAddText,
  onAddTextBox,
  onAddPage,
  onDeletePage,
  canDeletePage,
  onRegeneratePage,
  canRegeneratePage,
  onApplySafeLayout,
  onApplyPageTemplate,
  canApplySafeLayout,
  isApplyingSafeLayout,
  onCheckResolution
}) => {
  const [showTextBoxMenu, setShowTextBoxMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTextBoxMenu(false);
      }
    };

    if (showTextBoxMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTextBoxMenu]);

  const handleAddTextBox = (styleId: string) => {
    onAddTextBox(styleId);
    setShowTextBoxMenu(false);
  };

  return (
    <div className="toolbar">
      <div className="toolbar-group">
        <button className="btn btn-primary" onClick={onAddText}>
          + Add Text
        </button>
        
        <div className="dropdown-wrapper" ref={dropdownRef}>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowTextBoxMenu(!showTextBoxMenu)}
          >
            + Add Text Box
          </button>
          {showTextBoxMenu && (
            <div className="dropdown-menu">
              {TEXT_BOX_STYLES.filter(style => style.id !== 'normal').map(style => (
                <button
                  key={style.id}
                  className="dropdown-item"
                  onClick={() => handleAddTextBox(style.id)}
                >
                  <span className="style-preview">{style.preview}</span>
                  <span className="style-name">{style.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={onAddPage}>
          + Add Page
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={onDeletePage}
          disabled={!canDeletePage}
        >
          Delete Page
        </button>
        {canRegeneratePage && onRegeneratePage && (
          <button 
            className="btn btn-primary" 
            onClick={onRegeneratePage}
            title="Open plot modal to modify prompts and regenerate page"
          >
            AI Regenerate Page
          </button>
        )}
        {canApplySafeLayout && onApplySafeLayout && (
          <button
            className="btn btn-secondary"
            onClick={() => void onApplySafeLayout()}
            disabled={isApplyingSafeLayout}
            title="Reflow narration into a print-safe template and add text-only continuation pages when needed"
          >
            {isApplyingSafeLayout ? 'Reflowing Page…' : 'Reflow Page Text'}
          </button>
        )}
        {canApplySafeLayout && onApplyPageTemplate && (
          <select
            className="btn btn-secondary"
            aria-label="Apply page template"
            defaultValue=""
            disabled={isApplyingSafeLayout}
            onChange={(event) => {
              const template = event.target.value as Exclude<PageLayoutTemplate, 'custom'>;
              event.target.value = '';
              if (template) void onApplyPageTemplate(template);
            }}
            title="Apply a deterministic PDF-safe template to this page"
          >
            <option value="" disabled>Apply template…</option>
            <option value="full-bleed-caption">Full-bleed caption</option>
            <option value="image-top-text-bottom">Image top / text bottom</option>
            <option value="text-top-image-bottom">Text top / image bottom</option>
            <option value="split">Split page</option>
            <option value="dialogue">Dialogue panel</option>
            <option value="title">Title page</option>
            <option value="text-only">Text only</option>
          </select>
        )}
        {onCheckResolution && (
          <button
            className="btn btn-secondary"
            onClick={onCheckResolution}
            title="Check if images have enough resolution for print quality (300 DPI)"
          >
            Check Resolution
          </button>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
